import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  RACE_ETHNICITY_OPTIONS,
  RACE_ETHNICITY_OTHER_VALUE,
  getRaceEthnicityTriggerLabel,
  normalizeRaceEthnicityArray,
  toggleRaceEthnicityValue,
} from "../shared/utils/childProfileOptions";

const ITEM_HEIGHT_PX = 34;
const PANEL_PADDING_PX = 8;
const MAX_VISIBLE_ITEMS = 5;
const MIN_PANEL_HEIGHT_PX = 120;

function collectScrollContainers(fromEl) {
  const out = [];
  if (!fromEl) return out;
  let node = fromEl.parentElement;
  while (node && node !== document.documentElement) {
    const cs = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(cs.overflowX) || /(auto|scroll|overlay)/.test(cs.overflowY)) {
      out.push(node);
    }
    node = node.parentElement;
  }
  return out;
}

/** Fixed menu placement: prefer below trigger; flip above when cramped. Never cover the trigger row. */
function computeMenuFixed(anchor) {
  const rect = anchor.getBoundingClientRect();
  const maxMenuHeight = MAX_VISIBLE_ITEMS * ITEM_HEIGHT_PX + PANEL_PADDING_PX;
  const margin = 8;
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const width = Math.min(rect.width, window.innerWidth - margin * 2);
  const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));

  const openDown = spaceBelow >= MIN_PANEL_HEIGHT_PX || spaceBelow >= spaceAbove;
  if (openDown) {
    const maxHeight = Math.max(MIN_PANEL_HEIGHT_PX, Math.min(maxMenuHeight, spaceBelow));
    return {
      left,
      width,
      top: rect.bottom + 4,
      maxHeight: `${maxHeight}px`,
    };
  }

  const maxHeight = Math.max(MIN_PANEL_HEIGHT_PX, Math.min(maxMenuHeight, spaceAbove));
  const top = Math.max(margin, rect.top - maxHeight - 4);
  return {
    left,
    width,
    top,
    maxHeight: `${maxHeight}px`,
  };
}

/**
 * Required multi-select race / ethnicity field (dropdown with checkboxes).
 * Uses platform dropdown-option styling; portals menu above modals.
 */
export default function RaceEthnicityMultiSelect({
  idPrefix = "race-ethnicity",
  value,
  otherValue = "",
  onChange,
  onOtherChange,
  disabled = false,
  required = false,
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuPortalRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuFixed, setMenuFixed] = useState(null);
  const listboxId = useId();
  const triggerId = `${idPrefix}-trigger`;

  const selections = normalizeRaceEthnicityArray(value);
  const showOther = selections.includes(RACE_ETHNICITY_OTHER_VALUE);
  const triggerLabel = getRaceEthnicityTriggerLabel(selections, otherValue);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (menuPortalRef.current?.contains(e.target)) return;
      close();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onOutside);
    window.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      if (!open) setMenuFixed(null);
      return;
    }

    const update = () => {
      const anchor = triggerRef.current;
      if (!anchor) return;
      setMenuFixed(computeMenuFixed(anchor));
    };

    update();
    const scrollParents = collectScrollContainers(rootRef.current);
    scrollParents.forEach((n) => n.addEventListener("scroll", update, { passive: true }));
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      scrollParents.forEach((n) => n.removeEventListener("scroll", update));
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const menu = (
    <div
      id={listboxId}
      role="listbox"
      aria-multiselectable="true"
      aria-label="Race / Ethnicity"
      className="dropdown-panel race-ethnicity-multiselect-panel"
      style={{
        maxHeight: menuFixed?.maxHeight || `${MAX_VISIBLE_ITEMS * ITEM_HEIGHT_PX}px`,
        width: "100%",
      }}
    >
      {RACE_ETHNICITY_OPTIONS.map((opt) => {
        const checked = selections.includes(opt.value);
        const optionId = `${idPrefix}-${opt.value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
        return (
          <label key={opt.value} htmlFor={optionId} className="dropdown-option">
            <input
              id={optionId}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(toggleRaceEthnicityValue(selections, opt.value))}
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className="form-field race-ethnicity-multiselect"
      style={{ minWidth: 0, gridColumn: "1 / -1" }}
    >
      <label htmlFor={triggerId} id={`${idPrefix}-legend`}>
        Race / Ethnicity{required ? " *" : ""}
      </label>
      <div className="dropdown-wrap race-ethnicity-multiselect-wrap">
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          className="race-ethnicity-multiselect-trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
        >
          <span className={selections.length === 0 ? "race-ethnicity-multiselect-trigger__placeholder" : undefined}>
            {triggerLabel}
          </span>
          <span className="dropdown-arrow" aria-hidden="true">
            {open ? "▲" : "▼"}
          </span>
        </button>
      </div>
      {open &&
        menuFixed != null &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuPortalRef}
            className="race-ethnicity-multiselect-portal"
            style={{
              position: "fixed",
              left: menuFixed.left,
              top: menuFixed.top,
              width: menuFixed.width,
              zIndex: 12000,
            }}
          >
            {menu}
          </div>,
          document.body
        )}
      {showOther ? (
        <div style={{ marginTop: 10 }}>
          <label htmlFor={`${idPrefix}-other`} style={{ display: "block", marginBottom: 4 }}>
            Please specify *
          </label>
          <input
            id={`${idPrefix}-other`}
            className="input"
            type="text"
            disabled={disabled}
            value={otherValue || ""}
            onChange={(e) => onOtherChange(e.target.value)}
            required
          />
        </div>
      ) : null}
    </div>
  );
}
